/**
 * Storage Path Extractor Utility
 * Extracts storage paths from Firebase Storage URLs for use with signed URL generation
 */

/**
 * Extract storage path from Firebase Storage URL
 * @param {string} firebaseUrl - Firebase Storage URL
 * @returns {string|null} - Storage path or null if extraction fails
 */
export function extractStoragePathFromUrl(firebaseUrl) {
  if (!firebaseUrl || typeof firebaseUrl !== 'string') {
    return null;
  }

  try {
    // Handle Firebase Storage URLs that contain '/o/' 
    if (firebaseUrl.includes('/o/')) {
      const urlParts = firebaseUrl.split('/o/')[1];
      if (urlParts) {
        // Decode the URL-encoded path and remove query parameters
        const encodedPath = urlParts.split('?')[0];
        return decodeURIComponent(encodedPath);
      }
    }

    // Handle direct storage paths
    if (firebaseUrl.startsWith('gs://')) {
      // Remove the gs://bucket-name/ prefix
      const pathMatch = firebaseUrl.match(/gs:\/\/[^\/]+\/(.+)/);
      return pathMatch ? pathMatch[1] : null;
    }

    return null;
  } catch (error) {
    console.warn('Failed to extract storage path from URL:', firebaseUrl, error);
    return null;
  }
}

/**
 * Construct storage path for organization-level documents
 * @param {string} orgId - Organization ID
 * @param {string} fileName - File name
 * @returns {string} - Storage path
 */
export function constructOrgDocumentPath(orgId, fileName) {
  return `documents/${orgId}/org-level/${fileName}`;
}

/**
 * Construct storage path for CMT digital products
 * @param {string} cmtId - CMT ID
 * @param {string} productId - Product ID
 * @param {string} fileName - File name
 * @returns {string} - Storage path
 */
export function constructCmtProductPath(cmtId, productId, fileName) {
  return `cmt_digital_products/${cmtId}/${productId}/${fileName}`;
}

/**
 * Construct storage path for incident documents
 * @param {string} incidentId - Incident ID
 * @param {string} fileName - File name
 * @returns {string} - Storage path
 */
export function constructIncidentDocumentPath(incidentId, fileName) {
  return `incident-documents/${incidentId}/${fileName}`;
}

/**
 * Get storage path for a document with fallback construction
 * @param {object} document - Document object
 * @param {string} document.url - Document URL
 * @param {string} fallbackPath - Fallback storage path if extraction fails
 * @returns {string} - Storage path
 */
export function getDocumentStoragePath(document, fallbackPath) {
  if (!document) {
    return fallbackPath;
  }

  // Try to extract from URL first
  const extractedPath = extractStoragePathFromUrl(document.url);
  if (extractedPath) {
    return extractedPath;
  }

  // Return fallback path
  return fallbackPath;
} 